import React, {
	useState,
	useMemo,
	useEffect,
	useLayoutEffect,
	useRef,
	useCallback,
	type FC,
	type KeyboardEvent,
	type MouseEvent,
} from 'react';
import { styled } from '@compiled/react';
import pick from 'lodash/pick';
import DropdownMenu, {
	DropdownItemGroup,
	DropdownItem,
	type DropdownItemProps,
} from '@atlaskit/dropdown-menu';
import Icon from '@atlaskit/icon/glyph/addon';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { standardizeLocale } from '@atlassian/jira-common-constants/src/supported-locales';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { IconCustom } from '@atlassian/jira-icon-custom';
import { ErrorState } from '@atlassian/jira-issue-navigator-actions-common';
import {
	useAnalyticsEvents,
	fireOperationalAnalytics,
	fireUIAnalytics,
	FireUiAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import { usePluggableItemsQuery } from '../services/query';
import type { IssueNavigatorPluggableItemsQuery_jiraWebSection_webItems_nodes as PluggableItemType } from '../services/query/__generated_apollo__/IssueNavigatorPluggableItemsQuery';
import { CONTAINER_MIN_WIDTH } from './constants';
import { Empty } from './empty';
import { PluggableItemsFilterButton as FilterButton } from './filter-button';
import { buildStyleClass, isInOrIsIFrame, translateAppTypeToAnalyticsType } from './utils';

export const closeDialog = (): void => window.connectHost?.dialog?.close();
export const getOpenDialog = (): HTMLIFrameElement | undefined | null => {
	const topEl = window.AJS?.LayerManager?.global?.getTopLayer();
	if (topEl == null) {
		return null;
	}
	return topEl.find('iframe')?.[0];
};

export const useModalDialogCleanupEffect = (): void => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	return useEffect(
		() => () => {
			try {
				const dialogBeforeClose = getOpenDialog();
				closeDialog();
				const dialogNow = getOpenDialog();

				fireOperationalAnalytics(
					createAnalyticsEvent({}),
					dialogBeforeClose && dialogNow ? 'iframe not closed' : 'iframe closed',
					'apps',
					{
						// Match the unique key part separated by a final __ delimination
						uniqueKey: dialogBeforeClose?.id.match(/.*(?=__)/)?.[0] ?? dialogBeforeClose?.id,
						uniqueOpenKey: dialogNow?.id.match(/.*(?=__)/)?.[0] ?? dialogNow?.id,
					},
				);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					error,
					meta: {
						id: 'issue-navigator-action-pluggable-items.ui.useModalDialogCleanupEffect',
						packageName: 'jiraIssueNavigatorActionPluggableItems',
						teamName: 'empanada',
					},
				});
			}
		},
		[createAnalyticsEvent],
	);
};

export const useRegisterModalDialogAnalytics: () => () => void = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const iFrameEstablishedCallback = useCallback(
		(payload: { extension: { options: Record<string, unknown> } }) => {
			if (payload.extension.options.moduleType === 'dialogs') {
				fireOperationalAnalytics(
					createAnalyticsEvent({}),
					'iframe established',
					'apps',
					pick(payload.extension.options, [
						'addon_key',
						'isDialog',
						'dialogId',
						'isFullPage',
						'uniqueKey',
						'origin',
					]),
				);
			}
		},
		[createAnalyticsEvent],
	);
	const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | undefined>();
	const registerListeners = useCallback(() => {
		// Connect doesn't seem to have a way to notify when it has been registered, so we have to resort to polling
		timeoutIdRef.current = setTimeout(() => {
			if (!window.connectHost) {
				registerListeners();
			} else {
				window.connectHost.onIframeEstablished(iFrameEstablishedCallback);
			}
		}, 1000);
	}, [iFrameEstablishedCallback]);

	useLayoutEffect(
		() => () => {
			clearTimeout(timeoutIdRef.current);
			// @ts-expect-error - TS2339 - Unsafe use of Connect's internal API
			if (iFrameEstablishedCallback._wrapper != null) {
				window.connectHost?.offIframeEstablished(iFrameEstablishedCallback);
			}
			timeoutIdRef.current = undefined;
		},
		[iFrameEstablishedCallback, registerListeners],
	);

	return () => {
		if (timeoutIdRef.current === undefined) {
			registerListeners();
		}
	};
};

export const useInlineDialogZIndexOverrideLayoutEffect = (): void => {
	const { current: styleNode } = useRef<HTMLStyleElement>(document.createElement('style'));
	useLayoutEffect(() => {
		document.head?.appendChild(styleNode);
		styleNode.setAttribute('data-nin-pluggable-items', 'css');
		styleNode.sheet?.insertRule(
			'div.aui-inline-dialog[id^="inline-dialog-webitem-"]{z-index:401}',
			0,
		);
		return () => {
			document.head?.removeChild(styleNode);
		};
	}, [styleNode]);
};

type DropdownItemWithAnalyticsProps = DropdownItemProps & {
	id?: string;
	testId?: string;
	className?: string;
	// Analytics-only props
	analyticsAppName?: string;
	analyticsAppCount?: number;
	analyticsAppType?: string;
};

export const DropdownItemWithAnalytics: FC<DropdownItemWithAnalyticsProps> = ({
	analyticsAppName,
	analyticsAppCount,
	analyticsAppType,
	testId,
	...props
}: DropdownItemWithAnalyticsProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireDropdownClickUIAnalytics = useCallback(
		(event: KeyboardEvent<HTMLElement> | MouseEvent<HTMLElement>) => {
			if ('key' in event && event.key != null && event.key !== 'Enter' && event.key !== ' ') {
				return;
			}

			fireUIAnalytics(createAnalyticsEvent({}), 'dropdownItem clicked', 'apps', {
				uniqueKey: props.id,
				source: 'key' in event ? 'keypress' : 'click',
				appCount: analyticsAppCount,
				appName: analyticsAppName,
				appType: analyticsAppType,
			});
		},
		[analyticsAppCount, analyticsAppName, analyticsAppType, createAnalyticsEvent, props.id],
	);
	return (
		<div
			role="menuitem"
			tabIndex={0}
			onClick={fireDropdownClickUIAnalytics}
			onKeyPress={fireDropdownClickUIAnalytics}
			data-testid={testId}
		>
			<DropdownItem {...props} />
		</div>
	);
};

type PluggableItem = PluggableItemType | null | undefined;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PluggableItems: FC<Record<any, any>> = () => {
	const [fetchData, { loading, error, data }] = usePluggableItemsQuery();
	const totalCount = data?.jiraWebSection?.webItems?.totalCount ?? 0;
	const { locale } = useTenantContext();
	const [isOpen, setIsOpen] = useState(false);
	const allWebItemIds: string[] = useMemo(
		() => data?.jiraWebSection?.webItems?.nodes?.map((item) => item?.id).filter(Boolean) ?? [],
		[data],
	);
	const { current: intlCollator } = useRef(new Intl.Collator(standardizeLocale(locale)));
	const registerModalDialogAnalytics = useRegisterModalDialogAnalytics();
	useModalDialogCleanupEffect();
	useInlineDialogZIndexOverrideLayoutEffect();

	return (
		<DropdownMenu
			isLoading={loading}
			testId="issue-navigator-action-pluggable-items.ui.filter-button"
			trigger={({ triggerRef, ...props }) => <FilterButton {...props} ref={triggerRef} />}
			shouldFlip
			placement="bottom-end"
			isOpen={isOpen}
			onOpenChange={(attrs) => {
				// Ignore events originating from a matching iframe or an element inside an iframe
				const eventTarget = attrs.event?.target;
				if (
					eventTarget &&
					eventTarget instanceof HTMLElement &&
					isInOrIsIFrame(eventTarget, allWebItemIds)
				) {
					return;
				}

				if (attrs.isOpen) {
					if (!loading) {
						fetchData(error);
					}

					// Only setup analytics when the dialog is opened
					registerModalDialogAnalytics();
				}

				setIsOpen(attrs.isOpen);
			}}
			spacing="compact"
		>
			<Container>
				{!error && totalCount > 0 ? (
					<DropdownItemGroup>
						{[...(data?.jiraWebSection?.webItems?.nodes ?? [])]
							.sort(
								(a: PluggableItem, b: PluggableItem) =>
									(a?.label != null &&
										b?.label != null &&
										intlCollator.compare(a.label, b.label)) ||
									0,
							)
							.map(
								(item: PluggableItem) =>
									item && (
										<DropdownItemWithAnalytics
											testId="issue-navigator-action-pluggable-items.ui.drop-down-item"
											elemBefore={
												item.iconUrl != null ? (
													<IconCustom
														shouldChangeFillColor={false}
														url={item.iconUrl}
														ariaLabelledBy={item.id ?? undefined}
													/>
												) : (
													<Icon
														primaryColor={token('color.icon', colors.N500)}
														label=""
														testId="issue-navigator-action-pluggable-items.ui.default-icon"
													/>
												)
											}
											key={item.id}
											title={typeof item.tooltip === 'string' ? item.tooltip : undefined}
											// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
											className={item.target ? buildStyleClass(item.target) : ''}
											id={item.id ?? undefined}
											analyticsAppCount={totalCount}
											analyticsAppName={item.label ?? ''}
											analyticsAppType={translateAppTypeToAnalyticsType(item.target?.type)}
										>
											{item.label}
										</DropdownItemWithAnalytics>
									),
							)}
					</DropdownItemGroup>
				) : (
					<MaxWidthContainer>
						{error ? (
							<>
								<ErrorState pageAction="apps" onRetry={() => fetchData(true)} />
								<FireUiAnalytics
									action="viewed"
									actionSubject="errorDropdown"
									actionSubjectId="issueNavigatorActionsPluggableItems"
								/>
							</>
						) : (
							<Empty />
						)}
					</MaxWidthContainer>
				)}
			</Container>
		</DropdownMenu>
	);
};

export default PluggableItems;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minWidth: `${CONTAINER_MIN_WIDTH}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MaxWidthContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxWidth: `${CONTAINER_MIN_WIDTH}px`,
});
